import { useEffect, useRef, useState } from "react";
import type {
  OlderSymptomRegistrationsView,
  StaffPatientSymptomsView,
  StaffSymptomEntryView,
} from "@care-by-ella/contracts";
import {
  fetchPatientOlderSymptomRegistrations,
  fetchPatientSymptoms,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  hasPrivateSymptomReports,
  HISTORY_ENTRY_EXPLANATION,
  OLDER_SYMPTOM_NOTE,
  olderRegistrationHeading,
  PRIVATE_SYMPTOM_NOTE,
  safetyAnswerLines,
  safetyLevelLine,
  symptomEntryHeading,
  symptomUpdateProvenance,
  symptomUpdateSummary,
} from "@/lib/symptom-lines";

// TES-1252 (Ella 2.1 slice F) — "Besvär" on the patient overview: her
// symptom episodes, open and closed, every update over time with severity,
// trend, safety answers, raised rules, source and whether it was submitted
// or only saved by her (Ö-675). Her older-format F-04/F-05 answers sit
// behind one small "Tidigare besvär" link (TES-1447), shown only when there
// are any; opened, they are listed under "Äldre registreringar", each marked
// "Äldre format" and never mixed into the episodes (Ö-446).
//
// Doctor only, read only: both routes answer 403 to anyone else (Ö-423's
// grant; A-01 keeps symptom details out of every coach view), and every read
// is PDL-logged.

type Load<T> =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ok"; value: T };

function useLoad<T>(
  load: () => Promise<T>,
  key: string,
  onUnauthorized: () => void,
): Load<T> {
  const [result, setResult] = useState<Load<T>>({ state: "loading" });
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    setResult({ state: "loading" });
    loadRef
      .current()
      .then((value) => {
        if (live) setResult({ state: "ok", value });
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        setResult({ state: "error" });
      });
    return () => {
      live = false;
    };
  }, [key]);

  return result;
}

function Episode({ entry }: { entry: StaffSymptomEntryView }) {
  const heading = symptomEntryHeading(entry);
  return (
    <li className="flex flex-col gap-xs border-t border-surfaceMuted pt-sm first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline gap-x-sm">
        <span className="font-medium">{heading.title}</span>
        <span
          className={
            entry.status === "open"
              ? "text-xs font-medium"
              : "text-xs text-textSecondary"
          }
        >
          {heading.status}
        </span>
      </div>
      {entry.status === "history" ? (
        <span className="text-xs text-textSecondary">
          {HISTORY_ENTRY_EXPLANATION}
        </span>
      ) : null}
      <span className="text-xs text-textSecondary">{heading.meta}</span>
      <ol className="flex flex-col gap-xs pl-sm">
        {entry.updates.map((u) => {
          const safety = safetyLevelLine(u);
          return (
            <li key={u.id} className="flex flex-col">
              <span>{symptomUpdateSummary(u)}</span>
              <span className="text-xs text-textSecondary">
                {symptomUpdateProvenance(u)}
              </span>
              {safetyAnswerLines(u.safetyAnswers).map((line) => (
                <span key={line} className="text-xs text-textSecondary">
                  {line}
                </span>
              ))}
              {safety ? (
                <span className="text-xs font-medium">{safety}</span>
              ) : null}
              {u.note ? (
                <span className="text-xs text-textSecondary">
                  Kommentar: ”{u.note}”
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </li>
  );
}

// TES-1447 (Ö-699, Ö-701, Emelie 2026-10-19): "Om relevanta äldre besvär
// finns räcker en liten länk ”Tidigare besvär”, inte utfällda listor, kort
// eller stor modul i patient-/läkaröversikten. Historik, Äldre
// format-märkning och godkänd förklaring visas först när länken öppnas."
// The same rule and the same link text as her app.
const OLDER_SYMPTOMS_LINK = "Tidigare besvär";

function OlderRegistrations({ view }: { view: OlderSymptomRegistrationsView }) {
  const [open, setOpen] = useState(false);
  if (view.registrations.length === 0) return null;
  return (
    <div className="flex flex-col gap-xs">
      <button
        type="button"
        className="self-start text-xs underline"
        aria-expanded={open}
        aria-controls="older-symptom-registrations"
        onClick={() => setOpen((v) => !v)}
      >
        {OLDER_SYMPTOMS_LINK}
      </button>
      {open ? (
        <div id="older-symptom-registrations" className="flex flex-col gap-xs">
          <h3 className="text-sm font-medium">{view.title.sv}</h3>
          <p className="text-xs text-textSecondary">{OLDER_SYMPTOM_NOTE}</p>
          <ul className="flex flex-col gap-xs">
            {view.registrations.map((r) => (
              <li key={r.submissionId}>
                <details>
                  <summary className="cursor-pointer">
                    <span className="mr-sm rounded-sm bg-surfaceMuted px-xs text-xs">
                      {view.formatLabel.sv}
                    </span>
                    {olderRegistrationHeading(r)}
                  </summary>
                  <dl className="mt-xs flex flex-col gap-xs pl-sm">
                    {r.items.map((i) => (
                      <div key={i.fieldKey} className="flex flex-col">
                        <dt className="text-xs text-textSecondary">
                          {i.question.sv}
                        </dt>
                        <dd>{i.answer.sv}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function SymptomEpisodesPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const episodes = useLoad<StaffPatientSymptomsView>(
    () => fetchPatientSymptoms(patientId),
    patientId,
    onUnauthorized,
  );
  const older = useLoad<OlderSymptomRegistrationsView>(
    () => fetchPatientOlderSymptomRegistrations(patientId),
    patientId,
    onUnauthorized,
  );

  if (episodes.state === "loading" || older.state === "loading") {
    return <p className="text-xs text-textSecondary">Läser besvär …</p>;
  }

  const entries = episodes.state === "ok" ? episodes.value.entries : [];
  const olderCount =
    older.state === "ok" ? older.value.registrations.length : 0;

  return (
    <div className="flex flex-col gap-md text-sm">
      {episodes.state === "error" ? (
        <p className="text-xs text-textSecondary">
          Besvären kunde inte läsas. Ladda om sidan.
        </p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Inga besvär registrerade
          {olderCount > 0 ? " i den nya registreringen" : ""}.
        </p>
      ) : (
        <>
          {hasPrivateSymptomReports(entries) ? (
            <p className="text-xs text-textSecondary">{PRIVATE_SYMPTOM_NOTE}</p>
          ) : null}
          <ul className="flex flex-col gap-sm">
            {entries.map((e) => (
              <Episode key={e.id} entry={e} />
            ))}
          </ul>
        </>
      )}
      {older.state === "error" ? (
        <p className="text-xs text-textSecondary">
          Äldre registreringar kunde inte läsas. Ladda om sidan.
        </p>
      ) : older.state === "ok" ? (
        <OlderRegistrations view={older.value} />
      ) : null}
    </div>
  );
}
